// ─── Impostazioni → 💳 Borsellino, Acconto e Crediti ─────────────────────────
//
// Voce 36, mockup approvato il 27/09/2026 (nome consigliato, strada A). 🗣️
// *«dobbiamo accorpare in una voce unica Borsellino, crediti, acconto. E premio
// per chi organizza. Se no ci perdiamo.»* ⇒ Una voce sola del menu, dove prima
// erano due («Acconto» e «Premio a chi organizza»), e dentro quattro linguette.
//
// 🗂️ Come la Cassa, ogni linguetta è un INDIRIZZO, non uno stato della pagina:
// i vecchi `/impostazioni/caparra` e `/impostazioni/premio` portano dritti alla
// loro, e «indietro» nel browser torna dov'era.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { VALORE_CREDITO_CENTS, type ImpostazioniCrediti } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { Caparra } from './Caparra.js';
import { Premio } from './Premio.js';
import { SoloLettura, euro, usaModifica, usaRilettura } from './comune.js';

export type LinguettaBorsellino = 'borsellino' | 'acconto' | 'crediti' | 'premio';

const LINGUETTE: ReadonlyArray<{ chi: LinguettaBorsellino; dove: string; testo: string }> = [
  { chi: 'borsellino', dove: '/impostazioni/borsellino', testo: 'Borsellino' },
  { chi: 'acconto', dove: '/impostazioni/borsellino/acconto', testo: 'Acconto' },
  { chi: 'crediti', dove: '/impostazioni/borsellino/crediti', testo: 'Crediti' },
  { chi: 'premio', dove: '/impostazioni/borsellino/premio', testo: 'Premio a chi organizza' },
];

export function BorsellinoAccontoCrediti({
  linguetta,
}: {
  linguetta: LinguettaBorsellino;
}): React.JSX.Element {
  return (
    <section>
      <header className="testata">
        <h2>Borsellino, Acconto e Crediti</h2>
      </header>
      <nav className="linguette-cassa" aria-label="Cosa imposti">
        {LINGUETTE.map((l) => (
          <Link key={l.chi} to={l.dove} aria-current={linguetta === l.chi ? 'page' : undefined}>
            {l.testo}
          </Link>
        ))}
      </nav>
      {linguetta === 'borsellino' ? (
        <IlBorsellino />
      ) : linguetta === 'acconto' ? (
        <Caparra />
      ) : linguetta === 'crediti' ? (
        <Crediti />
      ) : (
        <Premio />
      )}
    </section>
  );
}

/** Cosa c'è nel borsellino. Gli euro non hanno regole: qui si dice dove si muovono. */
function IlBorsellino(): React.JSX.Element {
  return (
    <div className="riquadro borsellino-spiegato">
      <h3>Cosa c’è nel borsellino di un socio</h3>
      <div className="due-origini">
        <p>
          <strong>💶 Euro.</strong> Li carica in Segreteria e li usa per pagare le quote. Si caricano e si
          scaricano dalla scheda del socio, in <Link to="/soci">Soci</Link> → scheda → Borsellino; ogni
          movimento resta scritto lì e in Cassa. Non ci sono regole da impostare.
        </p>
        <p>
          <strong>🎟️ Crediti.</strong> 1 Credito = {euro(VALORE_CREDITO_CENTS)} di sconto sulla sua quota di
          una partita. Non si prelevano, non si rimborsano e non si passano ad altri. Arrivano dall’
          <Link to="/impostazioni/borsellino/acconto">acconto</Link> che torna e dal{' '}
          <Link to="/impostazioni/borsellino/premio">premio a chi organizza</Link>; quanto valgono sta in{' '}
          <Link to="/impostazioni/borsellino/crediti">Crediti</Link>.
        </p>
      </div>
    </div>
  );
}

// ── La linguetta «Crediti»: la regola sola ──────────────────────────────────

const CHIAVE = 'crediti-impostazioni';

type Modulo = { mesi: string; avviso: string };

const daSalvata = (s: ImpostazioniCrediti): Modulo => ({
  mesi: String(s.mesi),
  avviso: String(s.giorniAvviso),
});

const intero = (s: string, min: number, max: number): number | undefined => {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

function Crediti(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<ImpostazioniCrediti>('GET', '/settings/crediti'),
  });
  if (letta.isLoading) return <Attesa cosa="le regole dei Crediti" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <ModuloCrediti salvata={letta.data} modificabile={modificabile} />;
}

function ModuloCrediti({
  salvata,
  modificabile,
}: {
  salvata: ImpostazioniCrediti;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvata(salvata));
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setM(daSalvata(salvata)), [salvata]);
  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };

  const mesi = intero(m.mesi, 1, 60);
  const avviso = intero(m.avviso, 1, 120);
  const pronto = mesi !== undefined && avviso !== undefined;

  const salva = useMutation({
    meta: { fatto: 'regole dei Crediti salvate' },
    mutationFn: () => chiama<ImpostazioniCrediti>('PUT', '/settings/crediti', { mesi, giorniAvviso: avviso }),
    onSuccess: () => {
      setSalvato(true);
      rileggi();
    },
  });

  return (
    <>
      <div className="riquadro">
        <h3>🎟️ Da dove arrivano</h3>
        <div className="due-origini">
          <p>
            <strong>Acconto tornato.</strong> Una partita della sera disdetta in tempo, o annullata dal
            circolo: l’acconto torna come Crediti. Col campo vuoto, o disdetta all’ultimo, resta al circolo.
            Regole in <Link to="/impostazioni/borsellino/acconto">Acconto</Link>.
          </p>
          <p>
            <strong>Premio a chi organizza.</strong> Chi organizza una partita e ne ha un’altra entro 7
            giorni. Regole in <Link to="/impostazioni/borsellino/premio">Premio a chi organizza</Link>.
          </p>
        </div>
      </div>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto && modificabile) salva.mutate();
        }}
      >
        <SoloLettura mostra={modificabile} />
        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>⏳ Quanto valgono</h3>
          <Campo
            etichetta="I Crediti valgono (mesi)"
            inputMode="numeric"
            value={m.mesi}
            onChange={(e) => cambia((x) => ({ ...x, mesi: e.target.value }))}
          />
          {mesi === undefined && <p className="guasto">Fra 1 e 60 mesi.</p>}
          <Campo
            etichetta="Avviso dei Crediti in scadenza (giorni prima)"
            inputMode="numeric"
            value={m.avviso}
            onChange={(e) => cambia((x) => ({ ...x, avviso: e.target.value }))}
          />
          {avviso === undefined && <p className="guasto">Fra 1 e 120 giorni.</p>}
          <p className="tenue piccolo">
            Vale per tutti i Crediti, da qualunque parte arrivino. Cambiandola, i Crediti che nascono da quel
            momento prendono la regola nuova; quelli già dati tengono la loro data.
          </p>
        </fieldset>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && (
              <p className="tenue">
                Salvato. I Crediti nuovi valgono {mesi} {mesi === 1 ? 'mese' : 'mesi'}.
              </p>
            )}
            <button type="submit" disabled={salva.isPending || !pronto}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </>
  );
}
